import * as React from 'react';
import * as Clients from '../../client/ApiClient';
import { authentication } from '../../services/Authentication';
import { DataTable, DataRow, DataCell, DataTableButton } from '../basic/DataTable';
import Toolbar from '../basic/Toolbar';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Input, Button } from 'reactstrap';
import { RangePagination, RangePaginationState, paginate } from '../basic/Pagination';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { enqueueNotification } from "../../store/Notifications";
import { store } from "../../AppContext";
import CloseConfirmationModal from '../basic/modals/CloseConfirmationModal';
import ConfirmationModal from '../basic/modals/ConfirmationModal';
import * as FileTypes from '../../constants/FileUploadType';
import * as MiniConstants from '../../constants/MiniConversion';
import './Mini.scss';

interface IActivitiesPageState {
    activities: Clients.ActivitiesMiniConversionModel[];
    options: { [key: string]: Clients.ConversionOptionValueModel } | undefined;
    selectedFile: any;
    pagination: RangePaginationState;
    hubConnection: HubConnection | null;
    hubConnected: boolean;
    running: boolean;
    buttonLock: boolean;
    closeConfirmationModal: boolean,
    confirmationModal: boolean,
    errMessage: string,
    uploadId: string | null,
}

interface IActivitiesProps extends RouteComponentProps<{
    conversionId: string
}> {
    getOptions: () => void;
}


class ActivitiesPage extends React.Component<IActivitiesProps, IActivitiesPageState>{
    private readonly clientActivities: Clients.ActivitiesMiniConversionClient = new Clients.ActivitiesMiniConversionClient();
    private options: Clients.ConversionOptionsClient = new Clients.ConversionOptionsClient();
    private executions: Clients.ExecutionsClient = new Clients.ExecutionsClient();
    private conversionId: string = '';
    private actvFileQueued: boolean = false;
    private actvFileUploaded: boolean = false;
    private statusPollInterval: ReturnType<typeof setInterval> | null = null;

    constructor(props: Readonly<IActivitiesProps>) {
        super(props)
        this.state = {
            activities: [],
            options: undefined,
            selectedFile: null,
            pagination: {
                pageSize: 25,
                pageNumber: 0,
            },
            hubConnection: null,
            hubConnected: false,
            running: false,
            buttonLock: true,
            closeConfirmationModal: false,
            confirmationModal: false,
            errMessage: '',
            uploadId: null,
        }
    }

    componentDidMount() {
        this.conversionId = this.props.match.params.conversionId;
        this.getOptions();
        this.buttonLock();

        const link = `${process.env.PUBLIC_URL}/stagesignal`
        const hubConnection = new HubConnectionBuilder().withUrl(link).withAutomaticReconnect().build();
        this.setState({ hubConnection }, () => {
            this.state.hubConnection!
                .start()
                .then(() => {
                    console.log('[SignalR] Connected');
                    this.setState({ hubConnected: true });
                    this.state.hubConnection!.invoke('JoinConversionGroup', this.conversionId);
                    this.checkForInProgressUpload();
                })
                .catch(err => {
                    console.log('Error while establishing connection :(');
                    this.checkForInProgressUpload();
                });
            this.state.hubConnection!.on('ActivitiesComplete', (done: string) => {
                this.stopStatusPoll();
                this.setState({ running: false, uploadId: null });
                this.refreshList(true);
            });
            let reload = 0;
            this.state.hubConnection!.on('ActivitiesRunning', (running: string) => {
                reload += 1;
                if (reload === 15 || reload % 300 === 0) {
                    this.refreshList(false);
                }
                if (!this.state.running) {
                    this.setState({ running: true })
                }
            });
            this.state.hubConnection!.on('ActivitiesError', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("An error occurred. File cannot be uploaded.");
            });
            this.state.hubConnection!.on('ActivitiesEmptyFail', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("Uploaded Activities File is Empty.");
            });
            this.state.hubConnection!.on('ActivitiesFail', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("File is in incorrect orientation. Please see file example.");
            });
            this.state.hubConnection!.on('ActivitiesCustRefNumFail', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("Customer Ref # missing from one or more rows. File cannot be uploaded.");
            });
            this.state.hubConnection!.on('TransactionDateFail', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("Transaction Date/Time missing from one or more rows. File cannot be uploaded.");
            });
            this.state.hubConnection!.on('EnteredByEmpCodeFail', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("Entered by Employee Code missing from one or more rows. File cannot be uploaded.");
            });
            this.state.hubConnection!.on('ActivityActionFail', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("Activity Action missing from one or more rows. File cannot be uploaded.");
            });
            this.state.hubConnection!.on('ActivityDescFail', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("Activity Description missing from one or more rows. File cannot be uploaded.");
            });
            this.state.hubConnection!.on('ActivitiesWorkbookProtected', (done: string) => {
                this.stopStatusPoll();
                this.popErrMessage("Activities file has restricted access, is password protected or is not an Excel document. File cannot be uploaded.");
            });
            this.state.hubConnection!.onreconnected(() => {
                console.log('[SignalR] Reconnected');
                this.setState({ hubConnected: true });
                this.state.hubConnection!.invoke('JoinConversionGroup', this.conversionId);
                this.stopStatusPoll();
                if (this.state.running) {
                    this.refreshList(true);
                }
            });
            this.state.hubConnection!.onclose(() => {
                console.log('[SignalR] Disconnected');
                this.setState({ hubConnected: false });
                if (this.state.running && this.state.uploadId) {
                    this.startStatusPoll(this.state.uploadId);
                }
            });
            if (this.state.hubConnected && this.state.hubConnection!.state == HubConnectionState.Disconnected) {
                store.dispatch(enqueueNotification({ type: 'error', message: "User session has been disconnected." }));
                authentication.logout()
                    .then(() => this.props.history.push('/'))
                    .catch(error => { }
                    )
            };
        })
    }

    componentWillUnmount() {
        this.stopStatusPoll();
        if (this.state.hubConnection) {
            this.state.hubConnection.invoke('LeaveConversionGroup', this.conversionId).catch(() => {});
            this.state.hubConnection.stop();
        }
    }

    refreshList = (done: boolean) => {
        this.clientActivities.getAll(this.conversionId)
            .then(response => {
                this.setState({
                    activities: response!
                }, () => {
                    if (done) {
                        if (this.state.activities.length > 0) {
                            store.dispatch(enqueueNotification({ type: 'success', message: "Complete" }))
                            this.setOption(MiniConstants.conversionOption_ActvFileUploadedId, '1');
                            this.openCloseConfirmationModal();
                        } else {
                            this.setOption(MiniConstants.conversionOption_ActvFileUploadedId, '0');
                        }
                    }
                })
            })
            .catch(error => { console.log('Error loading Activities'); });
    }

    getOptions = () => {
        this.options.get(this.conversionId)
            .then(values => {
                const options = values!.reduce(
                    (result, current) => ({ ...result, [current.id!]: current, }), {});
                this.setState({ options });
            })
            .catch(e => {
                console.log(e)
            })
    }

    handleFileInput = (event: React.ChangeEvent) => {
        let selectedFile = (event.target as HTMLInputElement).files![0];
        if (selectedFile.type != "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
            (event.target as HTMLInputElement).value = '';
            store.dispatch(enqueueNotification({ type: 'error', message: "Please upload Excel files (.xlsx) only." }));
        }
        else {
            console.log("selected file changed");
            this.setState({ selectedFile });
        }
    };

    onFileUpload = () => {
        if (this.state.selectedFile === null) {
            store.dispatch(enqueueNotification({ type: 'error', message: "Please upload file." }))
        } else {
            if (this.actvFileUploaded) {
                this.setOption(MiniConstants.conversionOption_ActvFileUploadedId, '0');
                this.setOption(MiniConstants.conversionOption_ActvFileValidatedId, '0');
            }
            this.setState({ running: true });
            const formData = new FormData();
            const postUrl = '/api/v1/' + this.conversionId + '/fileUpload';

            formData.append("contentType", this.state.selectedFile!.type);
            formData.append("data", this.state.selectedFile!);
            formData.append("fileUploadTypeId", FileTypes.miniConversion_Activities);
            formData.append("name", this.state.selectedFile!.name);

            fetch(postUrl, {
                method: 'POST',
                body: formData
            })
                .then(response => {
                    if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
                    return response.json();
                })
                .then((data: any) => {
                    this.setState({ uploadId: data.id });
                    if (this.state.hubConnection?.state !== HubConnectionState.Connected) {
                        this.startStatusPoll(data.id);
                    }
                })
                .catch(error => {
                    console.log(error);
                    this.setState({ running: false });
                    store.dispatch(enqueueNotification({ type: 'error', message: 'Upload failed. Please try again.' }));
                })
        }
    }

    startStatusPoll = (uploadId: string) => {
        console.log('[SignalR] Starting status poll for upload:', uploadId);
        this.stopStatusPoll();
        this.statusPollInterval = setInterval(() => this.checkUploadStatus(uploadId), 3000);
    }

    stopStatusPoll = () => {
        if (this.statusPollInterval !== null) {
            clearInterval(this.statusPollInterval);
            this.statusPollInterval = null;
        }
    }

    checkUploadStatus = (uploadId: string) => {
        fetch(`/api/v1/${this.conversionId}/fileUpload/${uploadId}`)
            .then(r => r.json())
            .then((data: any) => {
                if (data.processingStatus !== null && data.processingStatus !== undefined) {
                    this.stopStatusPoll();
                    this.handleProcessingStatus(data.processingStatus);
                }
            })
            .catch(() => {});
    }

    handleProcessingStatus = (status: string) => {
        this.setState({ uploadId: null });
        switch (status) {
            case 'Complete':
                this.setState({ running: false });
                this.refreshList(true);
                break;
            case 'WorkbookProtected':
                this.popErrMessage("Activities file has restricted access, is password protected or is not an Excel document. File cannot be uploaded.");
                break;
            case 'Fail':
                this.popErrMessage("File is in incorrect orientation. Please see file example.");
                break;
            case 'EmptyFail':
                this.popErrMessage("Uploaded Activities File is Empty.");
                break;
            case 'CustRefNumFail':
                this.popErrMessage("Customer Ref # missing from one or more rows. File cannot be uploaded.");
                break;
            case 'TransactionDateFail':
                this.popErrMessage("Transaction Date/Time missing from one or more rows. File cannot be uploaded.");
                break;
            case 'EnteredByEmpCodeFail':
                this.popErrMessage("Entered by Employee Code missing from one or more rows. File cannot be uploaded.");
                break;
            case 'ActivityActionFail':
                this.popErrMessage("Activity Action missing from one or more rows. File cannot be uploaded.");
                break;
            case 'ActivityDescFail':
                this.popErrMessage("Activity Description missing from one or more rows. File cannot be uploaded.");
                break;
            case 'Error':
            default:
                this.popErrMessage("An error occurred. File cannot be uploaded.");
                break;
        }
    }

    checkForInProgressUpload = () => {
        fetch(`/api/v1/${this.conversionId}/fileUpload`)
            .then(r => r.json())
            .then((uploads: any[]) => {
                const myUploads = uploads
                    .filter((u: any) => u.fileUploadTypeId?.toLowerCase() === FileTypes.miniConversion_Activities.toLowerCase())
                    .sort((a: any, b: any) => new Date(b.uploadedOn).getTime() - new Date(a.uploadedOn).getTime());
                const latest = myUploads[0];
                if (latest && latest.processingStatus === null) {
                    this.setState({ running: true, uploadId: latest.id });
                    this.startStatusPoll(latest.id);
                }
            })
            .catch(() => {});
    }

    buttonLock = () => {
        this.executions.listExecutionsAllTierBoolean(this.conversionId)
            .then((response) => {
                if (response) {
                    this.setState({ buttonLock: response.isLocked })
                }
            })
            .catch(error => { })
    }

    openConfirmationModal = () => {
        this.setState({ confirmationModal: true })
    }

    openCloseConfirmationModal = () => {
        this.setState({ closeConfirmationModal: true })
    }

    refreshPage = (landingTab: string) => {
        window.location.reload();
        this.props.history.push({
            pathname: window.location.href.split('/').pop(),
            state: { tabName: landingTab }
        });
    }

    removeConfirmationModal = () => {
        this.setState({ closeConfirmationModal: false, confirmationModal: false }, () => { this.props.getOptions() });
    }

    setOption = (optionId: string, optionValue: string) => {
        const optionsClient: Clients.ConversionOptionsClient = new Clients.ConversionOptionsClient();
        const optionModel: Clients.ConversionOptionValueModel[] = [];
        const valuesArray: any = {
            conversionId: this.conversionId,
            value: optionValue,
            isLocked: false,
            id: optionId,
        };
        optionModel.push(valuesArray);
        optionsClient.update(this.conversionId, optionModel);
    }

    private popErrMessage = (message: string) => {
        this.setState({ running: false });
        if (this.state.activities.length > 0) { this.refreshList(false); }
        this.setState({ errMessage: message });
        this.openConfirmationModal();
    }


    public render() {
        if (this.state.options) {
            for (const id of Object.keys(this.state.options)) {
                const value = this.state.options[id].value;
                switch (id.toUpperCase()) {
                    case MiniConstants.conversionOption_ActvFileUploadedId: {
                        this.actvFileUploaded = (value === '1');
                        if (this.actvFileUploaded && (!this.state.activities || this.state.activities.length === 0)) {
                            this.refreshList(false);
                        }
                        break;
                    }
                    case MiniConstants.conversionOption_ActvFileQueuedId: {
                        this.actvFileQueued = (value === '1');
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }
        }

        return (
            <>
                <Toolbar>
                    <DataTableButton
                        icon='attachment'
                        id={`activitiesExample`}
                        title="activities Excel Example"
                        value="Download"
                        onClick={() => { window.open("/ActivitiesUploadFileTemplate.xlsx") }} />
                    <Input
                        type="file"
                        name="file"
                        id="exampleFile"
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        disabled={this.state.buttonLock || this.actvFileQueued}
                        onChange={e => this.handleFileInput(e)}
                    />
                    {this.state.running ?
                        < img className='loading-indicator' src={'loader.gif'} /> :
                        <Button
                            className='submit-button-orange'
                            disabled={this.state.buttonLock || this.actvFileQueued}
                            onClick={this.onFileUpload}>Upload
                        </Button>}
                </Toolbar>
                <DataTable>
                    <thead>
                        <tr>
                            <th className="number-column mini-column-mini">#</th>
                            <th className="mini-column-med">Customer Ref #</th>
                            <th className="mini-column-med">Policy Ref #</th>
                            <th className="mini-column">Transaction Date/Time</th>
                            <th className="mini-column">Entered by Employee Code</th>
                            <th className="mini-column">Activity Action</th>
                            <th className="mini-column">Activity Description</th>
                        </tr>
                    </thead>
                    <tbody className="nonEntity">
                        {paginate(this.state.activities, this.state.pagination, (activities, i) => (
                            <DataRow key={activities.customerRefNum}
                                id={`${activities.customerRefNum}`}
                            >
                                <DataCell className="number-column mini-column-mini">{activities.rowNumber}</DataCell>
                                <DataCell className="mini-column-med">{activities.customerRefNum}</DataCell>
                                <DataCell className="mini-column-med">{activities.policyRefNum}</DataCell>
                                <DataCell className="mini-column">{activities.transactionDate}</DataCell>
                                <DataCell className="mini-column">{activities.employeeCode}</DataCell>
                                <DataCell className="mini-column">{activities.action}</DataCell>
                                <DataCell className="mini-column">{activities.description}</DataCell>
                            </DataRow>
                        ))}
                    </tbody>
                </DataTable >
                <RangePagination
                    pageNumber={this.state.pagination.pageNumber}
                    pageSize={this.state.pagination.pageSize}
                    recordsCount={this.state.activities.length}
                    onChangeIndex={index => this.setState({
                        pagination: {
                            ...this.state.pagination,
                            pageNumber: index,
                        }
                    })}
                />
                <CloseConfirmationModal
                    isOpen={this.state.closeConfirmationModal}
                    title={'File Upload Completed'}
                    message={'Your file has been successfully uploaded.'}
                    onSubmit={this.removeConfirmationModal}
                />
                <ConfirmationModal
                    isOpen={this.state.confirmationModal}
                    message={this.state.errMessage}
                    onSubmit={this.removeConfirmationModal}
                />
            </>
        )
    }
}

export default withRouter(ActivitiesPage)